import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Platform Settings Service
 * Manages configurable platform settings like name and pricing
 */

const PLATFORM_SETTINGS_DOC = 'platform-settings';
let settingsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get platform settings from Firestore (with caching)
 * @returns {object} Platform settings
 */
export async function getPlatformSettings() {
  try {
    // Return cached settings if valid
    if (settingsCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      return settingsCache;
    }

    const settingsRef = doc(db, 'platformSettings', PLATFORM_SETTINGS_DOC);
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      const rawSettings = settingsSnap.data();
      
      // SECURITY: Filter out sensitive fields that should never be exposed to frontend
      // These fields are only accessible server-side via Cloud Functions
      const { rapidApiKey, stripeSecretKey, stripeWebhookSecret, openaiApiKey, anthropicApiKey, blsApiKey, ...safeSettings } = rawSettings;
      
      settingsCache = safeSettings;
      cacheTimestamp = Date.now();
      return settingsCache;
    }
    
    // Return defaults if settings don't exist
    const defaultSettings = getDefaultSettings();
    
    // Create default settings in Firestore
    await setDoc(settingsRef, {
      ...defaultSettings,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    settingsCache = defaultSettings;
    cacheTimestamp = Date.now();
    return defaultSettings;
  } catch (error) {
    console.error('❌ Error fetching platform settings:', error);
    // Return defaults on error
    return getDefaultSettings();
  }
}

/**
 * Update platform settings
 * @param {object} updates - Settings to update
 * @returns {boolean} Success status
 */
export async function updatePlatformSettings(updates) {
  try {
    const settingsRef = doc(db, 'platformSettings', PLATFORM_SETTINGS_DOC);
    
    await updateDoc(settingsRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    
    // Clear cache to force refresh
    settingsCache = null;
    cacheTimestamp = null;
    
    console.log('✅ Platform settings updated');
    return true;
  } catch (error) {
    console.error('❌ Error updating platform settings:', error);
    return false;
  }
}

/**
 * Get default platform settings
 * @returns {object} Default settings
 */
export function getDefaultSettings() {
  return {
    platformName: 'NeurolNet.ai',
    platformTagline: 'Your mind, decoded from your words.',
    reportPrice: 79, // USD in dollars - Cognitive Report
    careerReportPrice: 79, // USD in dollars - Career Path Report
    bundlePrice: 149, // USD in dollars - Both reports (save $9)
    currency: 'USD',
    betaMode: false,
    betaPrice: 29,
    allowNewUsers: true,
    allowCareerReports: true,
    maintenanceMode: false,
    supportEmail: 'support@neurolnet.ai',
    companyName: 'NeurolNet Inc.',
  };
}

/**
 * Clear settings cache (useful for testing)
 */
export function clearSettingsCache() {
  settingsCache = null;
  cacheTimestamp = null;
}

/**
 * Get current report price based on beta mode
 * @param {object} settings - Platform settings (optional, will fetch if not provided)
 * @returns {number} Current price in dollars
 */
export async function getCurrentReportPrice(settings = null) {
  if (!settings) {
    settings = await getPlatformSettings();
  }
  
  return settings.betaMode ? settings.betaPrice : settings.reportPrice;
}
